import { Box, Link, Stack, Typography } from "@mui/material";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import type { CorporateAttributes, CorporateBasicInfo } from "../types";
import { isHttpUrl } from "@/utils/link";
import { DataTable } from "./DataTable";
import { Fields } from "./Fields";
import { formatNumberOrDash, formatYenOrDash } from "../utils/format";

export const BasicInfoTab: React.FC<{
  basic: CorporateBasicInfo;
  attributes?: CorporateAttributes | null;
}> = ({ basic, attributes }) => {
  const w = attributes?.workplaceInfo ?? null;
  return (
    <Stack spacing={3}>
      <Fields
        items={[
          ["法人名", basic.name],
          ["フリガナ", basic.furigana],
          [
            "本店所在地",
            [basic.prefectureName, basic.cityName, basic.streetNumber]
              .filter(Boolean)
              .join("") || null,
          ],
          ["種別", basic.kindLabel],
          ["種別コード", basic.kind],
          ["指定日", basic.assignmentDate],
          ["法人番号（13桁）", basic.corporateNumber],
          ["会社法人等番号（12桁）", basic.corporateNumberLast12],
          ["閉鎖日", basic.closeDate],
        ]}
      />

      {/* gBiz由来の企業情報は基本情報タブ内にセクションとしてまとめ、出典を明記する。 */}
      {w && (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ color: "primary.main", display: "flex" }}>
              <Diversity3OutlinedIcon />
            </Box>
            <Typography variant="h6">職場情報（gBiz）</Typography>
          </Stack>
          <Fields
            items={[
              ["平均年齢", formatNumberOrDash(w.averageAge, "歳")],
              [
                "平均勤続年数",
                formatNumberOrDash(w.averageContinuousServiceYears, "年"),
              ],
              [
                "月平均所定外労働時間",
                formatNumberOrDash(
                  w.monthAveragePredeterminedOvertimeHours,
                  "時間"
                ),
              ],
              [
                "女性労働者割合",
                formatNumberOrDash(w.femaleWorkersProportion, "%"),
              ],
              // femaleShareOf* は割合ではなく人数（gBiz: 管理職/役員の女性数）。
              [
                "女性管理職数",
                formatNumberOrDash(w.femaleShareOfManager, "人"),
              ],
              ["女性役員数", formatNumberOrDash(w.femaleShareOfOfficers, "人")],
              [
                "女性育休取得者数",
                formatNumberOrDash(w.maternityLeaveAcquisitionNum, "人"),
              ],
              [
                "男性育休取得者数",
                formatNumberOrDash(w.paternityLeaveAcquisitionNum, "人"),
              ],
            ]}
          />
        </Box>
      )}

      {(attributes?.certifications ?? []).length > 0 && (
        <DataTable
          icon={<WorkspacePremiumOutlinedIcon />}
          title="認定（gBiz）"
          headers={["認定日", "名称", "対象", "所管府省"]}
          rows={(attributes?.certifications ?? []).map((c) => [
            c.dateOfApproval,
            c.title,
            c.target,
            c.governmentDepartments,
          ])}
        />
      )}

      {(attributes?.commendations ?? []).length > 0 && (
        <DataTable
          icon={<EmojiEventsOutlinedIcon />}
          title="表彰（gBiz）"
          headers={["表彰日", "名称", "対象", "区分", "所管府省"]}
          rows={(attributes?.commendations ?? []).map((c) => [
            c.dateOfCommendation,
            c.title,
            c.target,
            c.category,
            c.governmentDepartments,
          ])}
        />
      )}

      {(attributes?.patents ?? []).length > 0 && (
        <DataTable
          icon={<LightbulbOutlinedIcon />}
          title="特許（gBiz）"
          headers={["種別", "登録番号", "出願日", "分類", "名称"]}
          rows={(attributes?.patents ?? []).map((p) => [
            p.patentType,
            p.registrationNumber,
            p.applicationDate,
            p.classifications,
            isHttpUrl(p.url ?? undefined) ? (
              <Link
                href={p.url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                {p.title}
              </Link>
            ) : (
              p.title
            ),
          ])}
        />
      )}

      {(attributes?.subsidies ?? []).length > 0 && (
        <DataTable
          icon={<PaidOutlinedIcon />}
          title="補助金（gBiz）"
          headers={["交付日", "名称", "金額（円）", "対象", "所管府省"]}
          rows={(attributes?.subsidies ?? []).map((s) => [
            s.dateOfApproval,
            s.title,
            formatYenOrDash(s.amount),
            s.target,
            s.governmentDepartments,
          ])}
        />
      )}

      {(attributes?.procurements ?? []).length > 0 && (
        <DataTable
          icon={<ShoppingCartOutlinedIcon />}
          title="調達（受注実績・gBiz）"
          headers={["受注日", "件名", "金額（円）", "発注機関"]}
          rows={(attributes?.procurements ?? []).map((p) => [
            p.dateOfOrder,
            p.title,
            formatYenOrDash(p.amount),
            p.governmentDepartments,
          ])}
        />
      )}
    </Stack>
  );
};
